import './SonarMenu.scss';

import classNames from 'classnames';
import { FeatureFlag } from '@sonar-software/sonar-core';
import { getAmplitudeRouteEventId } from '@sonar-software/sonar-analytics';
import { MenuInfo } from 'rc-menu/es/interface';
import { MenuItemType } from 'antd/es/menu/interface';
import { useNavigate } from 'react-router-dom';

import { IsEnabledFeatureFlag, useAmplitude, useFeatureFlags } from '@hooks';
import { Menu, MenuProps } from 'antd';
import React, { Key, useCallback, useMemo } from 'react';

export type SonarMenuItem = Required<MenuProps>['items'][number] & {
	children?: Array<SonarMenuItem>;
	key: Key;
	feature?: FeatureFlag;
	icon?: React.ReactNode;
	id?: string;
	route?: string;
};

export interface SonarMenuProps extends Omit<MenuProps, 'items'> {
	items: Array<SonarMenuItem>;
}

function filterMenuItems(items: Array<SonarMenuItem>, isEnabled: IsEnabledFeatureFlag): Array<SonarMenuItem> {
	const results: Array<SonarMenuItem> = [];

	for (const item of items) {
		if (item.feature && !isEnabled(item.feature)) {
			continue;
		}

		const initChildCount = item.children?.length ?? 0;

		const entry = {
			...item,
			children: item.children ? filterMenuItems(item.children, isEnabled) : undefined
		};

		if (initChildCount && !entry.children?.length && !entry.route && !(entry as MenuItemType).onClick) {
			continue;
		}

		results.push(entry as SonarMenuItem);
	}

	return results;
}

function buildMenuItemLookup(items: Array<SonarMenuItem>): Record<string, SonarMenuItem> {
	const lookup: Record<string, SonarMenuItem> = {};

	return items.reduce((previous, current) => {
		if (current.key) {
			previous[ String(current.key) ] = current;
		}

		if (current.children) {
			return {
				...previous,
				...buildMenuItemLookup(current.children)
			};
		}

		return previous;
	}, lookup) as Record<string, SonarMenuItem>;
}

export const SonarMenu: React.FC<SonarMenuProps> = ({ className, items, onClick, ...rest }) => {
	const navigate = useNavigate();
	const logEvent = useAmplitude();
	const { isEnabled } = useFeatureFlags();

	const menuItems = useMemo(() => filterMenuItems(items, isEnabled), [ isEnabled, items ]);
	const menuItemLookup = useMemo(() => buildMenuItemLookup(menuItems), [ menuItems ]);

	const handleClick = useCallback(
		(info: MenuInfo) => {
			if (onClick) {
				onClick(info);
			}

			const menuItem = menuItemLookup[ info.key ];

			if (menuItem.route) {
				const eventId = getAmplitudeRouteEventId(menuItem.route);

				if (eventId) {
					logEvent(eventId);
				}

				navigate(menuItem.route);
			}
		},
		[ logEvent, menuItemLookup, navigate, onClick ]
	);

	return <Menu
		{ ...rest }
		className={ classNames('sonar-menu', className) }
		items={ menuItems }
		onClick={ handleClick } />;
};
